import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { apiClient } from '@/lib/api-client';
import { useCommon } from '@/contexts/common-context';
import { useOrganization } from '@/contexts/organization-context';
import type { Organization, PetrohqApiResponseListDealerDto } from '@/lib/api/data-contracts';
import type { ApprovalBox, ApprovalProgram, ApprovalView, MyProfile } from '@/types/approval';
import { APPLY_TYPES, VIEW_OPTIONS } from './approval-common';
import { Search } from 'lucide-react';

/**
 * 조회조건 필드. PRD 는 네 화면이 서로 다른 조합을 쓴다 - 공용 필터 바를 쓰면 없어야 할 칸이 생긴다.
 * B210 §3.1 / A330 §3 / B220 §3.1 / A340 §2.1.
 */
export type SearchField = 'org' | 'dept' | 'reqOrg' | 'period' | 'product' | 'applyType' | 'dealer';

export interface SearchDraft {
  orgCd: string;
  deptCd: string;
  from: string;
  to: string;
  productCode: string;
  applyType: string;
  dealerCode: string;
}

export const EMPTY_DRAFT: SearchDraft = {
  orgCd: '',
  deptCd: '',
  from: '',
  to: '',
  productCode: '',
  applyType: '',
  dealerCode: '',
};

interface ScreenSpec {
  fields: SearchField[];
  defaultView: ApprovalView;
}

/**
 * 화면별 조회조건·조회구분 기본값.
 *
 * A340 은 목업을 따른다 - PRD 표는 `reqCd`+`reqNm`(요청부서 코드↔명 상호연동 한 쌍)을
 * "요청건 선택 / 요청번호 직접 입력" 두 칸으로 잘못 적고 조회기간·거래처·적용구분을 빠뜨렸다.
 * 표가 지목한 `populateReqSelect()` 본문이 요청건이 아니라 사업장 목록을 채운다 (2026-09-16 결정).
 */
export const SCREEN: Record<string, ScreenSpec> = {
  'requests:store-price': { fields: ['org', 'period', 'product', 'applyType'], defaultView: 'ALL' },
  'inbox:store-price': { fields: ['dept', 'period', 'reqOrg', 'product', 'applyType'], defaultView: 'UNAPPROVED' },
  'requests:dealer-price': { fields: ['org', 'period', 'dealer', 'applyType'], defaultView: 'ALL' },
  'inbox:dealer-price': { fields: ['dept', 'period', 'reqOrg', 'dealer', 'applyType'], defaultView: 'UNAPPROVED' },
};

export const screenKey = (box: ApprovalBox, program: ApprovalProgram) => `${box}:${program}`;

export function screenSpec(box: ApprovalBox, program: ApprovalProgram): ScreenSpec {
  return SCREEN[screenKey(box, program)];
}

/**
 * PRD 조회조건 카드. 조회구분 라디오는 누르는 즉시 반영하고(목업 filterChange), 나머지 조건은 조회 버튼으로 확정한다.
 * 사업장·부서 목록은 로그인 사용자 스코프(`common/organization`)라 조회범위가 여기서 자연히 걸린다.
 *
 * PRD §4.1 의 "일반직원은 소속 사업장 고정, 팀장은 소속 팀" 은 역할 하드코딩이 아니라 직책의
 * 조회범위(`/me/profile`.searchScope)로 건다. PRD 는 web.job_title 신설 전에 작성된 문서다.
 */
export function ApprovalSearch({
  box,
  program,
  applied,
  view,
  profile,
  onApply,
}: {
  box: ApprovalBox;
  program: ApprovalProgram;
  applied: SearchDraft;
  view: ApprovalView;
  /** 로딩 중이면 null - 잠그지 않고 연 상태로 둔다 */
  profile: MyProfile | null;
  onApply: (changes: Record<string, string | null>) => void;
}) {
  const { fields } = screenSpec(box, program);
  const { organizations, loadOrganizations } = useOrganization();
  const { getOils } = useCommon();
  const [draft, setDraft] = useState(applied);

  // 결재 SPA 는 메인 쉘 밖이라 조직 목록을 아무도 안 불러 준다
  useEffect(() => {
    if (organizations.length === 0) loadOrganizations();
  }, [organizations.length, loadOrganizations]);

  const appliedKey = JSON.stringify(applied);
  useEffect(() => setDraft(applied), [appliedKey]);

  // 직책 조회범위가 입력칸을 고정한다 (B210 §4.1, A330 §4.1). 역할이 아니라 운영자가 정한 직책 속성이다
  const fixedOrg = profile?.searchScope === 'SELF' ? profile.orgCd : null;
  const fixedDept = profile?.searchScope === 'DEPT' ? profile.orgCd : null;
  // 조회범위를 모르는 동안은 잠가 둔다. 열어 뒀다가 잠기는 것보다 덜 놀란다.
  // /me/profile 이 실패하면 새로고침까지 잠긴 채로 남는데, 이 값은 접근 경계가 아니라
  // 입력 편의라 조회 자체는 서버 스코프로 계속 된다 - 반대로 열어 두는 쪽이 더 나쁘다
  const scopeUnknown = profile === null;
  const deptCd = fixedDept ?? draft.deptCd;
  const orgCd = fixedOrg ?? draft.orgCd;

  const allStores = useMemo(() => orgsOfType(organizations, 'STORE'), [organizations]);
  // 요청부서 목록은 조회범위(부서) 규칙을 그대로 따른다 (A330 §4.2-3). 부서 밖 코드는 매핑하지 않는다 (§4.2-4)
  const stores = useMemo(
    () => {
      if (!deptCd) return allStores;
      const under = new Set(storesUnderOrg(organizations, deptCd));
      return allStores.filter(o => under.has(o.orgCd!));
    },
    [deptCd, allStores, organizations]
  );
  const depts = useMemo(() => [...orgsOfType(organizations, 'ORGANIZATION'), ...orgsOfType(organizations, 'BUSINESS')], [organizations]);
  const oils = useMemo(() => uniqueOils(getOils()), [getOils]);
  const dealers = useDealers(fields.includes('dealer'), orgCd, allStores);

  const set = (key: keyof SearchDraft) => (value: string) => setDraft(prev => ({ ...prev, [key]: value }));


  const search = () =>
    onApply({
      orgCd: draft.orgCd || null,
      deptCd: draft.deptCd || null,
      from: draft.from || null,
      to: draft.to || null,
      productCode: draft.productCode || null,
      applyType: draft.applyType || null,
      dealerCode: draft.dealerCode || null,
      id: null,
    });

  return (
    <section className='rounded-lg border bg-card p-3'>
      <div className='flex flex-wrap items-end gap-x-4 gap-y-3'>
        {fields.includes('dept') && (
          <Field label='부서코드(조회범위)'>
            <NativeSelect value={deptCd} onChange={set('deptCd')} className='w-44' disabled={fixedDept !== null || scopeUnknown}>
              <option value=''>전체</option>
              {depts.map(d => (
                <option key={d.orgCd} value={d.orgCd}>
                  {d.orgName}
                </option>
              ))}
            </NativeSelect>
          </Field>
        )}

        {fields.includes('period') && (
          <Field label='조회기간'>
            <div className='flex items-center gap-1'>
              <Input type='date' className='h-9 w-36' value={draft.from} onChange={e => set('from')(e.target.value)} />
              <span className='text-muted-foreground'>~</span>
              <Input type='date' className='h-9 w-36' value={draft.to} onChange={e => set('to')(e.target.value)} />
            </div>
          </Field>
        )}

        {(fields.includes('org') || fields.includes('reqOrg')) && (
          <Field label={fields.includes('org') ? '사업장' : '요청부서'}>
            <CodeNamePair value={orgCd} options={stores} onChange={set('orgCd')} disabled={fixedOrg !== null || scopeUnknown} />
          </Field>
        )}

        {fields.includes('product') && (
          <Field label='상품명'>
            <NativeSelect value={draft.productCode} onChange={set('productCode')} className='w-40'>
              <option value=''>전체</option>
              {oils.map(o => (
                <option key={o.code} value={o.code}>
                  {o.name}
                </option>
              ))}
            </NativeSelect>
          </Field>
        )}

        {fields.includes('dealer') && (
          <Field label='거래처'>
            <NativeSelect value={draft.dealerCode} onChange={set('dealerCode')} className='w-44'>
              <option value=''>전체</option>
              {dealers.map(d => (
                <option key={d.code} value={d.code}>
                  {d.name}
                </option>
              ))}
            </NativeSelect>
          </Field>
        )}

        {fields.includes('applyType') && (
          <Field label='적용구분'>
            <NativeSelect value={draft.applyType} onChange={set('applyType')} className='w-40'>
              <option value=''>전체</option>
              {APPLY_TYPES.map(t => (
                <option key={t.code} value={t.code}>
                  {t.name}
                </option>
              ))}
            </NativeSelect>
          </Field>
        )}


        <Button size='sm' className='h-9' onClick={search}>
          <Search className='mr-1 size-4' />
          조회
        </Button>
      </div>

      <div className='mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 border-t pt-3'>
        <span className='text-xs font-medium text-muted-foreground'>조회구분</span>
        {VIEW_OPTIONS.map(option => (
          <label key={option.value} className='flex cursor-pointer items-center gap-1.5 text-sm'>
            <input
              type='radio'
              name='approval-view'
              className='size-3.5 accent-primary'
              checked={view === option.value}
              onChange={() => onApply({ view: option.value, id: null })}
            />
            {option.label}
          </label>
        ))}
      </div>
    </section>
  );
}

/**
 * 코드 입력 ↔ 명 선택 상호연동 (B210 §4.1, A330 §4.2).
 * 조회범위 밖 코드는 자동 매핑하지 않고 경고 테두리만 남긴다 - 권한 밖 조회를 시도했음을 알려야 한다.
 */
function CodeNamePair({
  value,
  options,
  onChange,
  disabled,
}: {
  value: string;
  options: Organization[];
  onChange: (code: string) => void;
  disabled?: boolean;
}) {
  const [code, setCode] = useState(value);
  useEffect(() => setCode(value), [value]);

  const known = options.some(o => o.orgCd === code);
  const unknown = code !== '' && !known;

  return (
    <div className='flex items-center gap-1'>
      <Input
        className={`h-9 w-28 ${unknown ? 'border-destructive focus-visible:ring-destructive' : ''}`}
        value={code}
        placeholder='코드'
        aria-invalid={unknown}
        disabled={disabled}
        title={unknown ? '조회범위에 없는 코드입니다' : undefined}
        onChange={e => {
          const next = e.target.value.trim();
          setCode(next);
          // 마스터에 있는 코드일 때만 조건으로 반영한다. 없는 코드는 경고만 남기고 매핑하지 않는다
          if (next === '' || options.some(o => o.orgCd === next)) onChange(next);
        }}
      />
      <NativeSelect
        value={known ? code : ''}
        onChange={next => {
          setCode(next);
          onChange(next);
        }}
        className='w-44'
        disabled={disabled}
      >
        <option value=''>전체</option>
        {options.map(o => (
          <option key={o.orgCd} value={o.orgCd}>
            {o.orgName}
          </option>
        ))}
      </NativeSelect>
    </div>
  );
}

/** 거래처는 선택한 사업장 소속만 노출한다 (B220 §3.1). 사업장을 안 고르면 내 전체 사업장 기준이다 */
export function useDealers(enabled: boolean, orgCd: string, stores: Organization[]) {
  const [dealers, setDealers] = useState<{ code: string; name: string }[]>([]);
  const storeCodes = orgCd ? [orgCd] : stores.map(s => s.orgCd!);
  const key = storeCodes.join(',');

  useEffect(() => {
    if (!enabled || storeCodes.length === 0) {
      setDealers([]);
      return;
    }
    let cancelled = false;
    const search = new URLSearchParams();
    storeCodes.forEach(code => search.append('storeCodeList', code));
    apiClient
      .get('master/cust', { searchParams: search })
      .json<PetrohqApiResponseListDealerDto>()
      .then(res => {
        if (cancelled) return;
        const seen = new Map<string, string>();
        for (const dealer of res.result ?? []) {
          if (dealer.dealCode && !seen.has(dealer.dealCode)) seen.set(dealer.dealCode, dealer.dealName ?? dealer.dealCode);
        }
        setDealers([...seen].map(([code, name]) => ({ code, name })));
      })
      .catch(() => !cancelled && setDealers([]));
    return () => {
      cancelled = true;
    };
  }, [enabled, key]);

  return dealers;
}

/** 조직 코드 아래의 모든 매장 코드. COMPANY·ORGANIZATION·BUSINESS 어느 레벨이든 트리를 끝까지 탄다 */
export function storesUnderOrg(organizations: Organization[], code: string): string[] {
  const stores: string[] = [];
  const seen = new Set<string>([code]);
  const walk = (parent: string) => {
    for (const o of organizations) {
      if (o.parentOrgCd !== parent || !o.orgCd || seen.has(o.orgCd)) continue;
      seen.add(o.orgCd);
      if (o.type === 'STORE') stores.push(o.orgCd);
      else walk(o.orgCd);
    }
  };
  walk(code);
  return stores;
}

export function orgsOfType(organizations: Organization[], type: string): Organization[] {
  return organizations.filter(o => o.type === type && o.orgCd && o.orgName);
}

export function uniqueOils(oils: { oilCode?: string; oilName?: string }[]): { code: string; name: string }[] {
  const seen = new Map<string, string>();
  for (const oil of oils) {
    if (oil?.oilCode && !seen.has(oil.oilCode)) seen.set(oil.oilCode, oil.oilName ?? oil.oilCode);
  }
  return [...seen].map(([code, name]) => ({ code, name }));
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className='space-y-1'>
      <Label className='text-xs text-muted-foreground'>{label}</Label>
      {children}
    </div>
  );
}

/** 조회조건은 값이 코드라 네이티브 select 로 충분하다 (Radix Select 는 option 수가 늘면 무겁다) */
function NativeSelect({
  value,
  onChange,
  className,
  children,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  children: ReactNode;
  disabled?: boolean;
}) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      disabled={disabled}
      className={`h-9 rounded-md border border-input bg-background px-2 text-sm disabled:cursor-not-allowed disabled:opacity-60 ${className ?? ''}`}
    >
      {children}
    </select>
  );
}
